import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem/MenuItem';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField/TextField';
import React, { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormControlLabel } from '@mui/material';
import { PeriodicityLimitationEnum } from '@loyalty/shared-common/rule-api-client';
import { QlfNumberField } from '@qualifio/ui-commons/dist/components/qlf-number-field/QlfNumberField';
import { useTranslation } from 'react-i18next';
import { BurningRuleFormValue } from '../../burning-rules/form';
import { EarningRuleFormValue } from '../../earning-rules/form';
import { useEventSourcePeriodicity } from '../rule-table-constants';

interface Props {
  disabled: boolean;
}

export function PeriodicityLimitationForm({ disabled }: Props): React.ReactElement {
  const { t } = useTranslation();
  const sourceTranslationsPeriodicity = useEventSourcePeriodicity();
  const { control } = useFormContext<EarningRuleFormValue | BurningRuleFormValue>();

  const periodicityLimitationOptions = useMemo(
    () =>
      Object.values(PeriodicityLimitationEnum).map(value => ({
        value,
        text: sourceTranslationsPeriodicity[value],
      })),
    [t],
  );

  return (
    <Box display="flex" alignItems="center">
      <Box display="flex" flexShrink="1" flexGrow="0">
        <Controller
          control={control}
          name="options.periodicityLimitation.enabled"
          render={({ field: { ref, ...field } }) => (
            <FormControlLabel
              control={
                <Switch
                  {...field}
                  disabled={disabled}
                  inputRef={ref}
                  sx={{ marginBottom: 0 }}
                  checked={field.value}
                />
              }
              label={t('earning-rules.options.periodicityLimitation.label') as string}
            />
          )}
        />
      </Box>
      <Box display="flex" flex="1" alignItems="center">
        <Controller
          control={control}
          name="options.periodicityLimitation.occurrence"
          render={({ field: { ref, ...field } }) => (
            <QlfNumberField
              {...field}
              inputRef={ref}
              disabled={disabled}
              sx={{ minWidth: 100, maxWidth: 100 }}
            />
          )}
        />
        <Box component="p" sx={{ marginRight: 0.8, marginLeft: 0.8, color: '#666666' }}>
          {t('earning-rules.earnings.words.times')}
        </Box>
        <Controller
          control={control}
          name="options.periodicityLimitation.periodicity"
          render={({ field: { ref, ...field } }) => (
            <TextField {...field} select inputRef={ref} disabled={disabled}>
              {periodicityLimitationOptions.map(({ value, text }) => (
                <MenuItem key={value} value={value}>
                  {text}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </Box>
    </Box>
  );
}
